/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import {
  AbstractControl,
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from '@angular/forms';
import { LeadDocumentType } from '@interfaces';
import { IdentificationDocumentType } from '@types';
import { PalleteColor } from 'nanaykuna-ui-kit/loader';

@Component({
  selector: 'unicon-document-field',
  templateUrl: './document-field.component.html',
  styleUrl: './document-field.component.scss',
})
export class DocumentFieldComponent implements OnInit, AfterViewInit {
  @Input() value?: string;
  @Input() documentType!: number;
  @Input() documentTypes: LeadDocumentType[] = [];
  @Input() isActivated: boolean = true;
  @Output() result: EventEmitter<Record<string, string>> = new EventEmitter<
    Record<string, string>
  >();
  @ViewChild('documentFieldElement') formField!: ElementRef<HTMLFormElement>;
  @ViewChild('animationContainer')
  animationContainer!: ElementRef<HTMLDivElement>;
  protected edit: boolean = false;
  protected hovered: boolean = false;
  protected form!: FormGroup;
  protected maxLength: number = 8;

  constructor(
    private formBuilder: FormBuilder,
    private cdRef: ChangeDetectorRef,
  ) {
    this.setForm();
  }

  ngOnInit(): void {
    this.setInitialValuesAndValidations();
  }

  ngAfterViewInit(): void {
    this.setEditListeners();
  }

  private setForm(): void {
    this.form = this.formBuilder.group({
      inputType: new FormControl<string>(
        { disabled: true, value: '' },
        Validators.compose([Validators.required]),
      ),
      input: new FormControl<string>({ disabled: true, value: '' }),
    });
  }

  private setInitialValuesAndValidations(): void {
    if (this.value) {
      this.form.controls['input'].setValue(this.value);
      this.cdRef.detectChanges();
    }

    if (this.documentType) {
      this.form.controls['inputType'].setValue(this.documentType);
      this.cdRef.detectChanges();
    }

    this.form
      .get('inputType')
      ?.valueChanges.subscribe((inputTypeValue: string) => {
        this.setValidatorsBasedOnInputType(inputTypeValue);
      });
  }

  private setValidatorsBasedOnInputType(inputTypeValue: string): void {
    const inputControl: AbstractControl<any, any> = this.form.get(
      'input',
    ) as AbstractControl<any, any>;

    switch (parseInt(inputTypeValue, 10)) {
      case 1:
        inputControl?.setValidators([
          Validators.required,
          Validators.pattern(/^\d{8}$/),
        ]);
        this.maxLength = 8;
        break;
      case 2:
        inputControl?.setValidators([
          Validators.required,
          Validators.pattern(/^(10|20)\d{9}$/),
        ]);
        this.maxLength = 11;
        break;
      case 3:
        inputControl?.setValidators([
          Validators.required,
          Validators.pattern(/^[a-zA-Z0-9]{1,12}$/),
        ]);
        this.maxLength = 12;
        break;
      default:
        inputControl?.setValidators([Validators.required]);
        this.form.get('input')?.setValue('');
        break;
    }

    inputControl?.updateValueAndValidity();
  }

  private setEditListeners(): void {
    const formComponent: HTMLFormElement = this.formField.nativeElement;
    formComponent?.addEventListener('mouseenter', () => {
      this.hovered = true;
    });
    formComponent?.addEventListener('mouseleave', () => {
      this.hovered = false;
    });
  }

  protected getDocumentTypeName(
    documentType: string,
  ): IdentificationDocumentType | undefined {
    const doc = this.documentTypes.find(
      (item: LeadDocumentType) => item.id === parseInt(documentType, 10),
    ) as unknown as LeadDocumentType;
    if (doc) {
      return doc.name;
    }
    return;
  }

  protected getIconColor(): PalleteColor {
    if (this.form.errors) {
      return 'critical-300';
    }

    if (this.edit) {
      return 'neutrals-900';
    }

    if (this.hovered) {
      return 'neutrals-900';
    }

    return 'neutrals-500';
  }

  protected getFormIconColor(inputName: string): PalleteColor {
    if (this.form.get(inputName)?.errors) {
      return 'critical-300';
    }

    return 'neutrals-900';
  }

  protected onEdit(): void {
    this.form.enable();
    this.edit = true;
  }

  protected onCancel(): void {
    this.edit = false;
    this.hovered = false;
  }

  protected onSubmit(): void {
    this.result.emit(this.form.value);
    this.edit = false;
  }
}
